import React, { useEffect, useRef, useState } from 'react';
import { Download, RotateCcw } from 'lucide-react';
import { focusRing, glassPanel, secondaryButton } from '../../utils/demoUi';
type HistoryControlsProps = {
  /** Domain name used in labels, e.g. "Research". */
  domainName: string;
  exportLabel: string;
  onExport: () => void;
  onReset: () => void;
};
export function HistoryControls({
  domainName,
  exportLabel,
  onExport,
  onReset
}: HistoryControlsProps) {
  const [confirming, setConfirming] = useState(false);
  const [message, setMessage] = useState('');
  const confirmRef = useRef<HTMLButtonElement>(null);
  const resetRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirming) confirmRef.current?.focus();
  }, [confirming]);
  return <section aria-labelledby="demo-controls-title" className={`${glassPanel} p-6`} data-id="element-471" data-mp="3rbmjmnl9o53k">
      <h2 id="demo-controls-title" className="font-heading text-lg font-bold text-ink-dark" data-id="element-472" data-mp="1amzc7eomw1nx">
        Demo controls
      </h2>
      <p className="mt-1 text-sm leading-relaxed text-ink-muted" data-id="element-473" data-mp="16orqs6j86c6e">
        Export or reset only the {domainName} demonstration. Other domains keep their records.
      </p>
      <div className="mt-5 flex flex-wrap gap-3" data-id="element-474" data-mp="29b9qaxmrrami">
        <button type="button" onClick={() => {
        onExport();
        setMessage(`${domainName} records exported as JSON.`);
      }} className={secondaryButton} data-id="element-475" data-mp="2defqbnjag5kj">
          <Download size={16} aria-hidden="true" data-id="element-476" data-mp="1lnjmtdj4bd9x" />
          {exportLabel}
        </button>
        <button ref={resetRef} type="button" onClick={() => {
        setMessage('');
        setConfirming(true);
      }} className={secondaryButton} data-id="element-477" data-mp="1po3liljcaoy6">
          <RotateCcw size={16} aria-hidden="true" data-id="element-478" data-mp="1zeog7xvw79bt" />
          Reset {domainName} demo
        </button>
      </div>

      {confirming && <div role="alertdialog" aria-labelledby="reset-confirm-title" aria-describedby="reset-confirm-body" className="mt-5 rounded-xl border border-rose-200 bg-rose-50 p-5" data-id="element-479" data-mp="11yr44yduzaks">
          <p id="reset-confirm-title" className="font-semibold text-ink-dark" data-id="element-480" data-mp="2a7q9edimyw6s">
            Reset the {domainName} demo?
          </p>
          <p id="reset-confirm-body" className="mt-1 text-sm leading-relaxed text-ink-dark" data-id="element-481" data-mp="257u50d6112qp">
            All {domainName} records and drafts return to the original sample state. This cannot be undone. No other domain is affected.
          </p>
          <div className="mt-4 flex flex-wrap gap-3" data-id="element-482" data-mp="1r9do5teg9vuu">
            <button ref={confirmRef} type="button" onClick={() => {
          onReset();
          setConfirming(false);
          setMessage(`${domainName} demo reset to its original sample records.`);
        }} className={`inline-flex items-center justify-center gap-2 rounded-md bg-rose-700 px-5 py-2.5 font-semibold text-white hover:bg-rose-800 transition-colors duration-150 whitespace-nowrap ${focusRing}`} data-id="element-483" data-mp="1rc9otphomymz">
              Confirm reset
            </button>
            <button type="button" onClick={() => {
          setConfirming(false);
          resetRef.current?.focus();
        }} className={secondaryButton} data-id="element-484" data-mp="3f6v7mhqjiben">
              Cancel
            </button>
          </div>
        </div>}
      <p role="status" className="mt-3 text-sm font-medium text-navy" data-id="element-485" data-mp="3a9mn4ahx5va8">
        {message}
      </p>
    </section>;
}